/**
 * 通用 WebSocket 客户端类
 * 独立于 React，支持自动重连（指数退避）
 * 可复用于任何 WebSocket 场景
 */

export type ConnectionStatus = 'connecting' | 'connected' | 'disconnected' | 'reconnecting' | 'failed';

export interface WebSocketClientOptions {
  url: string;
  maxRetries?: number;      // 默认 Infinity
  baseDelay?: number;       // 默认 1000ms
  maxDelay?: number;        // 默认 2000ms
  onOpen?: () => void;
  onMessage?: (data: unknown) => void;
  onClose?: (event: CloseEvent) => void;
  onError?: (event: Event) => void;
  onStatusChange?: (status: ConnectionStatus) => void;
  onRetry?: (retryCount: number) => void;  // 重连时回调，传入当前重试次数
  silent?: boolean;        // true 时不输出控制台日志，业务层自行处理
  reconnectOnError?: boolean; // true 时创建失败或 error 事件后也按重连策略继续连接
}

const DEFAULT_OPTIONS = {
  maxRetries: Infinity,  // 无限重连
  baseDelay: 1000,
  maxDelay: 2000,        // 最大延迟 2 秒
};

export class WebSocketClient {
  private ws: WebSocket | null = null;
  private url: string;
  private maxRetries: number;
  private baseDelay: number;
  private maxDelay: number;
  private onOpen?: () => void;
  private onMessage?: (data: unknown) => void;
  private onClose?: (event: CloseEvent) => void;
  private onError?: (event: Event) => void;
  private onStatusChange?: (status: ConnectionStatus) => void;
  private onRetry?: (retryCount: number) => void;
  private silent: boolean;
  private reconnectOnError: boolean;

  private retryCount = 0;
  private retryTimeout: ReturnType<typeof setTimeout> | null = null;
  private isManualClose = false;
  private _status: ConnectionStatus = 'disconnected';

  constructor(options: WebSocketClientOptions) {
    this.url = options.url;
    this.maxRetries = options.maxRetries ?? DEFAULT_OPTIONS.maxRetries;
    this.baseDelay = options.baseDelay ?? DEFAULT_OPTIONS.baseDelay;
    this.maxDelay = options.maxDelay ?? DEFAULT_OPTIONS.maxDelay;
    this.onOpen = options.onOpen;
    this.onMessage = options.onMessage;
    this.onClose = options.onClose;
    this.onError = options.onError;
    this.onStatusChange = options.onStatusChange;
    this.onRetry = options.onRetry;
    this.silent = options.silent ?? false;
    this.reconnectOnError = options.reconnectOnError ?? false;
  }

  /**
   * 获取当前连接状态
   */
  get status(): ConnectionStatus {
    return this._status;
  }

  /**
   * 更新状态并触发回调
   */
  private updateStatus(status: ConnectionStatus): void {
    this._status = status;
    this.onStatusChange?.(status);
  }

  /**
   * 计算重连延迟（指数退避）
   */
  private getRetryDelay(): number {
    const delay = this.baseDelay * Math.pow(2, this.retryCount);
    return Math.min(delay, this.maxDelay);
  }

  /**
   * 按当前重连策略安排下一次连接。
   */
  private scheduleReconnect(): void {
    if (this.retryTimeout || this.isManualClose) {
      return;
    }

    if (this.retryCount < this.maxRetries) {
      const delay = this.getRetryDelay();
      this.retryCount += 1;
      if (!this.silent) {
        console.log(
          `[WebSocketClient] Reconnecting in ${delay}ms (attempt ${this.retryCount})`
        );
      }

      this.updateStatus('reconnecting');
      this.onRetry?.(this.retryCount);

      this.retryTimeout = setTimeout(() => {
        this.retryTimeout = null;
        this.connect();
      }, delay);
      return;
    }

    if (!this.silent) {
      console.log('[WebSocketClient] Max retries reached');
    }
    this.updateStatus('failed');
  }

  /**
   * 清理当前连接
   */
  private cleanup(): void {
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
      this.retryTimeout = null;
    }
    if (this.ws) {
      this.ws.onopen = null;
      this.ws.onmessage = null;
      this.ws.onerror = null;
      this.ws.onclose = null;
      if (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING) {
        this.ws.close();
      }
      this.ws = null;
    }
  }

  /**
   * 建立连接
   */
  connect(): void {
    // SSR 环境检查
    if (typeof WebSocket === 'undefined') {
      if (!this.silent) {
        console.warn('[WebSocketClient] WebSocket is not available (SSR environment)');
      }
      return;
    }

    // 清理旧连接
    this.cleanup();

    this.isManualClose = false;
    this.updateStatus('connecting');

    try {
      this.ws = new WebSocket(this.url);
    } catch (error) {
      if (!this.silent) {
        console.error('[WebSocketClient] Failed to create WebSocket:', error);
      }
      if (this.reconnectOnError) {
        this.scheduleReconnect();
      } else {
        this.updateStatus('failed');
      }
      return;
    }

    this.ws.onopen = () => {
      if (!this.silent) {
        console.log('[WebSocketClient] Connected');
      }
      this.retryCount = 0;
      this.updateStatus('connected');
      this.onOpen?.();
    };

    this.ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        this.onMessage?.(data);
      } catch (error) {
        if (!this.silent) {
          console.error('[WebSocketClient] JSON 解析失败，已按原始消息转发:', {
            error,
            data: event.data,
          });
        }
        // 如果不是 JSON，直接传递原始数据
        this.onMessage?.(event.data);
      }
    };

    this.ws.onerror = (event) => {
      if (!this.silent) {
        console.error('[WebSocketClient] Error:', event);
      }
      this.onError?.(event);
      if (this.reconnectOnError && !this.isManualClose) {
        this.cleanup();
        this.scheduleReconnect();
      }
    };

    this.ws.onclose = (event) => {
      if (!this.silent) {
        console.log('[WebSocketClient] Closed, code:', event.code, 'reason:', event.reason);
      }
      this.onClose?.(event);

      // 手动关闭或正常关闭，不重连
      if (this.isManualClose || event.code === 1000) {
        this.updateStatus('disconnected');
        return;
      }

      // 自动重连
      this.scheduleReconnect();
    };
  }

  /**
   * 断开连接
   */
  disconnect(): void {
    this.isManualClose = true;
    this.cleanup();
    this.updateStatus('disconnected');
  }

  /**
   * 手动重连（重置重试计数）
   */
  reconnect(): void {
    this.retryCount = 0;
    this.connect();
  }

  /**
   * 发送原始数据
   */
  send(data: string | ArrayBufferLike | Blob | ArrayBufferView): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      throw new Error('WebSocket 未连接');
    }
    this.ws.send(data);
  }

  /**
   * 发送 JSON 数据
   */
  sendJson(data: object): void {
    this.send(JSON.stringify(data));
  }

  /**
   * 检查是否已连接
   */
  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }
}

export default WebSocketClient;
